<script lang="ts">
	import CreditCard from '@lucide/svelte/icons/credit-card'
	import Quote from '@lucide/svelte/icons/quote'
	import { Image } from '@unpic/svelte'
	import AutoScroll from 'embla-carousel-auto-scroll'

	import { resolve } from '$app/paths'
	import * as Avatar from '#lib/components/ui/avatar/index.js'
	import { Button } from '#lib/components/ui/button/index.js'
	import * as Card from '#lib/components/ui/card/index.js'
	import * as Carousel from '#lib/components/ui/carousel/index.js'
	import { Separator } from '#lib/components/ui/separator/index.js'
	import { MENU_SECTIONS_MAPPING } from '#lib/constants/menuItems.js'
	import { phg } from '#lib/utils/posthog.js'

	const MEDIA_COVERAGE = [
		{
			name: 'Google Cloud',
			href: 'https://cloud.google.com/blog/topics/financial-services/how-blockchain-network-keeta-processes-11-million-transactions-per-second-with-spanner',
			src: 'https://res.cloudinary.com/dcruwhddg/image/upload/landing/google-cloud.svg',
		},
		{
			name: 'Forbes',
			href: 'https://www.forbes.com/advisor/au/investing/cryptocurrency/sei-cryptocurrency-explained/',
			src: 'https://res.cloudinary.com/dcruwhddg/image/upload/landing/forbes.svg',
		},
		{
			name: 'Yahoo Finance',
			href: 'https://finance.yahoo.com/news/bsv-association-partners-chainspect-showcase-111000155.html',
			src: 'https://res.cloudinary.com/dcruwhddg/image/upload/landing/yahoo-finance.svg',
		},
		{
			name: 'PayPal',
			href: 'https://developer.paypal.com/community/blog/pyusd-on-arbitrum/',
			src: 'https://res.cloudinary.com/dcruwhddg/image/upload/landing/paypal.svg',
		},
		{
			name: '247wallst',
			href: 'https://247wallst.com/investing/2025/11/18/solana-developer-growth-hits-record-highs-why-it-matters/',
			src: 'https://res.cloudinary.com/dcruwhddg/image/upload/landing/247wallst.svg',
		},
		{
			name: 'Grokipedia',
			href: 'https://grokipedia.com/page/Ethereum',
			src: 'https://res.cloudinary.com/dcruwhddg/image/upload/landing/grokipedia.svg',
		},
		{
			name: 'Mexc',
			href: 'https://www.mexc.com/learn/article/solana-blockchain-development-company-services-use-cases-and-how-to-choose/1',
			src: 'https://res.cloudinary.com/dcruwhddg/image/upload/landing/mexc.svg',
		},
		{
			name: 'Bybit',
			href: 'https://www.bybit.com/en/wiki/article/what-is-near-token-tps-sharding-comparison/',
			src: 'https://res.cloudinary.com/dcruwhddg/image/upload/landing/bybit.svg',
		},
		{
			name: 'Crypto.com',
			href: 'https://crypto.com/eea/university/polkadot-vs-avalanche-comparing-two-leading-multichain-protocols',
			src: 'https://res.cloudinary.com/dcruwhddg/image/upload/landing/crypto-com.svg',
		},
		{
			name: 'Bitget',
			href: 'https://www.bitget.com/academy/ethereum-dencun-upgrade',
			src: 'https://res.cloudinary.com/dcruwhddg/image/upload/landing/bitget.svg',
		},
		{
			name: 'BeInCrypto',
			href: 'https://beincrypto.com/ethereum-gas-limit-increase-eip/',
			src: 'https://res.cloudinary.com/dcruwhddg/image/upload/landing/beincrypto.svg',
		},
		{
			name: 'Kucoin',
			href: 'https://www.kucoin.com/blog/my-kucoin-ventures-weekly-report-fusaka-s-silent-upgrade-wall-street-s-2026-playbook-and-the-real-business-of-crypto-infrastructure',
			src: 'https://res.cloudinary.com/dcruwhddg/image/upload/landing/kucoin.svg',
		},
		{
			name: 'Benzinga',
			href: 'https://www.benzinga.com/crypto/cryptocurrency/25/10/48360245/cardano-outpaces-bitcoin-ethereum-in-decentralization-but-ada-stuck-below-65-cents',
			src: 'https://res.cloudinary.com/dcruwhddg/image/upload/landing/benzinga.svg',
		},
		{
			name: 'TheStreet',
			href: 'https://www.thestreet.com/crypto/investing/aptos-crosses-4-5-billion-total-transactions-as-daily-activity-hits-record',
			src: 'https://res.cloudinary.com/dcruwhddg/image/upload/landing/thestreet.svg',
		},
		{
			name: 'Seeking Alpha',
			href: 'https://seekingalpha.com/article/4914161-sharplink-treat-it-like-an-eth-cyclical',
			src: 'https://res.cloudinary.com/dcruwhddg/image/upload/landing/seeking-alpha.svg',
		},
		{
			name: 'CoinMarketCap',
			href: 'https://coinmarketcap.com/academy/article/introduction-to-unichain-defragmenting-defi-on-ethereum',
			src: 'https://res.cloudinary.com/dcruwhddg/image/upload/landing/cmc.svg',
		},
		{
			name: 'Investopedia',
			href: 'https://www.investopedia.com/understanding-spot-ethereum-etfs-8622726',
			src: 'https://res.cloudinary.com/dcruwhddg/image/upload/landing/investopedia.svg',
		},
		{
			name: 'CoinDesk',
			href: 'https://www.coindesk.com/markets/2024/05/17/fantom-outperforms-coindesk-20-over-past-week-as-tvl-jumps',
			src: 'https://res.cloudinary.com/dcruwhddg/image/upload/landing/coindesk.svg',
		},
		{
			name: 'Fortune',
			href: 'https://fortune.com/crypto/2025/02/11/uniswap-labs-opens-hyper-fast-blockchain-unichain-to-the-public-as-l2-race-heats-up/',
			src: 'https://res.cloudinary.com/dcruwhddg/image/upload/landing/fortune.svg',
		},
		{
			name: 'Cointelegraph',
			href: 'https://cointelegraph.com/magazine/zora-ethereum-l2-base-solana-killer',
			src: 'https://res.cloudinary.com/dcruwhddg/image/upload/landing/cointelegraph.svg',
		},
		{
			name: 'Bloomberg',
			href: 'https://www.bloomberg.com/news/articles/2024-05-29/paypal-adds-pyusd-stablecoin-to-memecoin-favorite-solana-blockchain',
			src: 'https://res.cloudinary.com/dcruwhddg/image/upload/landing/bloomberg.svg',
		},
	]

	const TESTIMONIALS = [
		{
			href: 'https://x.com/lucianmincu',
			text: 'Chainspect turns performance into our strongest marketing. It’s great to finally have a tool built for people who read logs, not headlines.',
			name: 'Lucian Mincu',
			position: 'Co-founder & CIO at MultiversX',
			avatarSrc: 'https://res.cloudinary.com/dcruwhddg/image/upload/landing/lucian-mincu.jpg',
			logoAlt: 'IoTeX',
			logoSrc: 'https://res.cloudinary.com/dcruwhddg/image/upload/logos/multiversx.svg',
			logoWidth: 148,
		},
		{
			href: 'https://x.com/Raullen',
			text: 'Without Chainspect, you’re building in a bubble. Its dashboard and Compare tool help us see how our project stands against other chains.',
			name: 'Raullen Chai',
			position: 'Founder & CEO at IoTeX',
			avatarSrc: 'https://res.cloudinary.com/dcruwhddg/image/upload/landing/raullen-chai.jpg',
			logoAlt: 'IoTeX',
			logoSrc: 'https://res.cloudinary.com/dcruwhddg/image/upload/logos/iotex.svg',
			logoWidth: 88,
		},
		{
			href: 'https://x.com/michaelfkong',
			text: 'Chainspect helps the community look behind the hype and boasts at what really matters — the fundamentals',
			name: 'Michael Kong',
			position: 'Founder & CEO at Sonic',
			avatarSrc: 'https://res.cloudinary.com/dcruwhddg/image/upload/landing/michael-kong.jpg',
			logoAlt: 'Sonic',
			logoSrc: 'https://res.cloudinary.com/dcruwhddg/image/upload/logos/sonic.svg',
			logoWidth: 77,
		},
		{
			href: 'https://x.com/JanCamenisch',
			text: 'No hype, just data — that’s the kind of attitude Web3 is often missing, and it’s what Chainspect brings to the table. The openness and visibility it unlocks are hard to underestimate.',
			name: 'Jan Camenisch',
			position: 'CTO at ICP',
			avatarSrc: 'https://res.cloudinary.com/dcruwhddg/image/upload/landing/jan-camenisch.jpg',
			logoAlt: 'Kaia',
			logoSrc: 'https://res.cloudinary.com/dcruwhddg/image/upload/logos/icp.svg',
			logoWidth: 137,
		},
		{
			href: 'https://x.com/seo_sangmin',
			text: 'Chainspect makes it easier to take a look under the hood of any blockchain, helping users, builders, and investors make more informed decisions',
			name: 'Dr. Sam Seo',
			position: 'Chairman at Kaia',
			avatarSrc: 'https://res.cloudinary.com/dcruwhddg/image/upload/landing/sam-seo.jpg',
			logoAlt: 'Kaia',
			logoSrc: 'https://res.cloudinary.com/dcruwhddg/image/upload/logos/kaia.svg',
			logoWidth: 74,
		},
		{
			href: 'https://x.com/VincentGramoli',
			text: "Understanding blockchain performance is difficult so the Chainspect service is very helpful. By monitoring the maximum performance blockchain technologies reach in production, Chainspect help demystify how layer-1 and layer-2's (among others) perform in reality.",
			name: 'Prof. Vincent Gramoli',
			position: 'Founder & CTO at Redbelly Network',
			avatarSrc: 'https://res.cloudinary.com/dcruwhddg/image/upload/landing/vincent-gramoli.jpg',
			logoAlt: 'Rebelly Network',
			logoSrc: 'https://res.cloudinary.com/dcruwhddg/image/upload/logos/redbelly.svg',
			logoWidth: 85,
		},
		{
			href: 'https://x.com/tomerweller',
			text: "Putting real usage, not marketing materials, front and center is what Web3 is missing, and it's what Chainspect brings to the table. The openness and visibility it unlocks cannot be overestimated.",
			name: 'Tomer Weller',
			position: 'CPO at Stellar',
			avatarSrc: 'https://res.cloudinary.com/dcruwhddg/image/upload/landing/tomer-weller.jpg',
			logoAlt: 'Stellar',
			logoSrc: 'https://res.cloudinary.com/dcruwhddg/image/upload/logos/stellar.svg',
			logoWidth: 94,
		},
		{
			href: 'https://x.com/BasementNodes',
			text: 'No matter how diligent you are, self-reported stats look best when confirmed by a third party. In this regard, Chainspect is a great benchmark that helps with building trust with the community and developers.',
			name: 'Cordt Hanson',
			position: 'DevRel Lead at Sei Foundation',
			avatarSrc: 'https://res.cloudinary.com/dcruwhddg/image/upload/landing/cordt-hanson.jpg',
			logoAlt: 'Sei',
			logoSrc: 'https://res.cloudinary.com/dcruwhddg/image/upload/logos/sei.svg',
			logoWidth: 64,
		},
	]

	const STATS = [
		{
			value: '100+',
			label: 'Metrics',
		},
		{
			value: '50+',
			label: 'Networks',
		},
		{
			value: '7TB+',
			label: 'Data Processed Daily',
		},
	]

	let innerWidth = $state(0)
</script>

{#snippet ctas(place: 'main_banner' | 'bottom_banner')}
	<Button
		href={resolve('dashboard/crypto-cards')}
		onclick={() => phg.capture('open_dashboard_cards', { place })}
		size="lg"
		variant="brand"
	>
		<CreditCard size="20" />

		Crypto Cards
	</Button>
{/snippet}

<svelte:window bind:innerWidth />

<main class="pt-4 pb-24 sm:pt-6 sm:pb-28">
	<section class="container flex flex-col items-center gap-6 px-4 text-center sm:pt-28">
		<div
			class="flex min-h-[calc(100svh-6rem)] flex-col items-start justify-center gap-8 sm:min-h-[unset] sm:items-center sm:gap-6"
		>
			<h1
				class="2xs:text-5xl text-left text-4xl leading-[1.05] font-bold sm:text-center sm:text-6xl"
			>
				Web3's Top Blockchain
				<br />
				Fundamentals Tracker
			</h1>

			<h2
				class="links-inside text-muted-foreground xs:max-w-xl text-left text-lg font-semibold sm:text-center sm:text-base sm:font-medium"
			>
				Real-time blockchain analytics for investors, researchers, and builders.
				<br />
				No paywalls, just data.
			</h2>

			{@render ctas('main_banner')}
		</div>

		<div
			class="absolute top-0 right-0 -z-1 h-[160vh] w-full bg-[radial-gradient(circle_farthest-side_at_100%_10%,rgba(133,134,173,0.15),rgba(9,9,11,0)_89%)]"
		></div>

		<div class="relative max-w-full sm:mt-20">
			<Image
				alt="Chainspect on desktop screenshot"
				crossorigin="anonymous"
				decoding="async"
				fetchpriority="high"
				height={474}
				loading="eager"
				priority
				src="https://res.cloudinary.com/dcruwhddg/image/upload/landing/macbook"
				width={780}
			/>

			<div class="absolute right-1 bottom-0 w-1/4 sm:right-4">
				<Image
					alt="Chainspect on mobile screenshot"
					crossorigin="anonymous"
					decoding="async"
					fetchpriority="high"
					height={376}
					loading="eager"
					priority
					src="https://res.cloudinary.com/dcruwhddg/image/upload/landing/iphone"
					width={187}
				/>
			</div>
		</div>
	</section>

	<section class="container flex flex-col items-center px-4 pt-24 sm:pt-40">
		<h3 class="max-w-2xl text-center text-2xl font-bold sm:text-4xl">
			Fundamental Blockchain Analysis Available for Everybody
		</h3>

		<p class="text-muted-foreground mt-6 max-w-2xl px-1 text-center text-base sm:px-0">
			Blockchains are all about transparency, that's why we provide unbiased technical information
			about all popular networks with zero paywalls.
		</p>

		<div
			class="mt-10 grid w-full grid-cols-1 grid-rows-[1fr_auto_1fr_auto_1fr] items-center justify-center gap-6 sm:mt-14 sm:grid-cols-[1fr_auto_1fr_auto_1fr] sm:grid-rows-1 sm:gap-4"
		>
			{#each STATS as { value, label }, index (label)}
				<div class="flex flex-col items-center justify-center">
					<p class="text-center text-5xl font-semibold sm:text-6xl">{value}</p>

					<p class="text-muted-foreground mt-4 text-center text-base md:text-lg">{label}</p>
				</div>

				{#if index < STATS.length - 1}
					<Separator
						class="mx-auto h-px w-10/12 sm:h-full sm:w-px"
						orientation={innerWidth < 640 ? 'horizontal' : 'vertical'}
					/>
				{/if}
			{/each}
		</div>
	</section>

	<section class="flex flex-col items-center pt-24 sm:pt-40">
		<h3 class="px-4 text-center text-2xl font-bold sm:mb-2 sm:text-4xl">
			Referenced by Leading Organizations
		</h3>

		<Carousel.Root
			class="mt-6 max-w-full"
			opts={{
				loop: true,
			}}
			plugins={[
				AutoScroll({
					speed: 0.5,
					startDelay: 0,
					playOnInit: true,
					direction: 'backward',
					stopOnInteraction: false,
					stopOnMouseEnter: true,
					stopOnFocusIn: true,
				}),
			]}
		>
			<Carousel.Content>
				{#each MEDIA_COVERAGE as { name, href, src } (name)}
					<Carousel.Item class="basis-1/2 md:basis-1/4 lg:basis-1/6">
						<a
							class="flex items-center justify-center rounded transition-all hover:scale-101 focus-visible:ring-2"
							aria-label="Link to {name}"
							{href}
							onclick={() => phg.capture('media_coverage_click', { value: href })}
							rel="external noopener noreferrer"
							target="_blank"
						>
							<img
								alt={name}
								crossorigin="anonymous"
								decoding="async"
								fetchpriority="low"
								height="100"
								loading="lazy"
								{src}
								width="200"
							/>
						</a>
					</Carousel.Item>
				{/each}
			</Carousel.Content>

			<div
				class="from-background pointer-events-none absolute inset-0 bg-linear-to-r from-0% via-transparent via-10%"
			></div>
			<div
				class="from-background pointer-events-none absolute inset-0 bg-linear-to-l from-0% via-transparent via-10%"
			></div>
		</Carousel.Root>
	</section>

	<section class="flex flex-col items-center px-4 pt-32 sm:pt-40 xl:container">
		<h3 class="mb-5 px-4 text-center text-2xl font-bold sm:mb-10 sm:text-4xl">
			Explore Every Dimension of Web3
		</h3>

		<div
			class="bg-border grid w-full gap-px overflow-hidden rounded-xl border shadow lg:grid-cols-2 xl:grid-cols-4"
		>
			{#each [...MENU_SECTIONS_MAPPING.dashboards.items, ...MENU_SECTIONS_MAPPING.agents.items, ...MENU_SECTIONS_MAPPING.cards.items].filter(item => !!item.description) as { route, Icon, text, description } (route)}
				<a
					class="hover:bg-accent bg-primary-foreground p-3 transition-colors sm:p-5 md:p-6"
					href={resolve(route)}
					onclick={() => phg.capture('dashboard_grid_click', { value: route })}
				>
					<div
						class="mb-3 flex flex-row items-center justify-start gap-2 sm:mb-2 sm:flex-col sm:items-start sm:gap-5 md:gap-6"
					>
						<Icon class="size-6 sm:size-7 md:size-8" size="32" />

						<p class="text-left text-xl font-bold md:text-2xl">{text}</p>
					</div>

					{#if description}
						<p class="text-muted-foreground text-sm sm:text-base">{description}</p>
					{/if}
				</a>
			{/each}
		</div>
	</section>

	<section class="flex flex-col items-center pt-24 sm:pt-40">
		<h3 class="max-w-2xl px-4 text-center text-2xl font-bold sm:text-4xl">
			Tested. Trusted. Proven.
		</h3>

		<Carousel.Root
			class="mt-10 max-w-full"
			opts={{
				loop: true,
				dragFree: true,
				align: 'start',
			}}
			plugins={[
				AutoScroll({
					speed: 0.5,
					startDelay: 0,
					playOnInit: true,
					direction: 'forward',
					stopOnInteraction: false,
					stopOnMouseEnter: true,
					stopOnFocusIn: true,
				}),
			]}
		>
			<Carousel.Content class="py-1">
				{#each TESTIMONIALS as { href, text, name, position, avatarSrc, logoAlt, logoSrc, logoWidth } (href)}
					<Carousel.Item class="basis-full md:basis-1/2 xl:basis-1/3">
						<Card.Root class="relative h-full">
							<Quote class="text-muted-foreground absolute top-4 right-3" size="14" />

							<Card.Content class="pt-6">
								<p>
									{text}
								</p>
							</Card.Content>

							<Card.Footer class="mt-auto flex items-center justify-between">
								<a
									class="flex items-center justify-start gap-3"
									{href}
									rel="external noopener noreferrer"
									target="_blank"
								>
									<Avatar.Root>
										<Image
											class="aspect-square size-full rounded-full object-cover"
											alt={name}
											crossorigin="anonymous"
											decoding="async"
											fetchpriority="low"
											height={40}
											loading="lazy"
											priority
											src={avatarSrc}
											width={40}
										/>
									</Avatar.Root>

									<div>
										<p class="text-sm font-bold">{name}</p>
										<p class="text-muted-foreground text-xs font-medium">
											{position}
										</p>
									</div>
								</a>

								<img
									alt={logoAlt}
									crossorigin="anonymous"
									decoding="async"
									fetchpriority="low"
									height={24}
									loading="lazy"
									src={logoSrc}
									width={logoWidth}
								/>
							</Card.Footer>
						</Card.Root>
					</Carousel.Item>
				{/each}
			</Carousel.Content>

			<div
				class="from-background pointer-events-none absolute inset-0 bg-linear-to-r from-0% via-transparent via-10%"
			></div>
			<div
				class="from-background pointer-events-none absolute inset-0 bg-linear-to-l from-0% via-transparent via-10%"
			></div>
		</Carousel.Root>
	</section>

	<section class="container flex flex-col items-center px-4 pt-24 sm:pt-40">
		<h3 class="text-center text-2xl font-bold sm:text-4xl">Navigate Crypto with Confidence</h3>

		<p class="text-muted-foreground mt-6 mb-8 text-center text-base">
			Understand blockchain numbers. Gain insights. Stay ahead.
		</p>

		{@render ctas('bottom_banner')}
	</section>
</main>
